import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import type { IsoDate } from '../lib/dates';

/**
 * Calendar data.
 *
 * One request per visible range, and one cached request for the reference data
 * (staff, categories) that does not change as you page through weeks. The
 * legacy month view issued a query per day and re-ran its double-booking check
 * once per staff member inside a PHP loop; the whole point of this hook is that
 * paging a week forward is a single round trip.
 */

export interface CalendarEvent {
    readonly id: number;
    readonly jobNumber: string | null;
    readonly clientName: string | null;
    readonly siteAddress: string | null;
    readonly categoryId: number | null;
    readonly colour: string | null;
    readonly notes: string | null;
    /** The booking window shown in the job's row header. */
    readonly startsOn: IsoDate | null;
    readonly endsOn: IsoDate | null;
    /** Non-null = off the default board but kept, with its crew history. */
    readonly archivedAt: string | null;
}

export interface CalendarAssignment {
    readonly id: number;
    readonly eventId: number;
    readonly staffId: number | null;
    readonly date: IsoDate;
    readonly startTime: string | null;
    readonly endTime: string | null;
}

export interface CalendarRange {
    readonly from: IsoDate;
    readonly to: IsoDate;
    readonly events: readonly CalendarEvent[];
    readonly assignments: readonly CalendarAssignment[];
    readonly clashes: Readonly<Record<string, readonly IsoDate[]>>;
    readonly truncated: boolean;
}

export interface StaffSummary {
    readonly staffId: number;
    readonly name: string;
    readonly titleId: number | null;
    readonly titleName: string | null;
    /** Skillset colour. Follows the person, not the job. */
    readonly colour: string | null;
    readonly textColour: string | null;
    /** False = "Do not show as staff on calendar". Optional: an older API omits it. */
    readonly showOnCalendar?: boolean;
}

export interface CalendarCategory {
    readonly id: number;
    readonly name: string;
    readonly colour: string | null;
    readonly isLeave: boolean;
}

/** Assignments indexed for O(1) cell lookup: `${eventId}|${date}`. */
export type CellIndex = ReadonlyMap<string, readonly CalendarAssignment[]>;

/** The grid is job rows by day columns, so a cell is a job and a date. */
export function eventCellKey(eventId: number, date: IsoDate): string {
    return `${String(eventId)}|${date}`;
}

interface CalendarState {
    readonly range: CalendarRange | null;
    readonly staff: readonly StaffSummary[];
    readonly categories: readonly CalendarCategory[];
    readonly eventsById: ReadonlyMap<number, CalendarEvent>;
    readonly staffById: ReadonlyMap<number, StaffSummary>;
    readonly cells: CellIndex;
    readonly loading: boolean;
    /** True only on the very first load, so paging does not blank the grid. */
    readonly initialising: boolean;
    readonly error: string | null;
    reload: () => void;
}

/** Reference data outlives any one range, so it is fetched once per session. */
let referenceCache: { staff: readonly StaffSummary[]; categories: readonly CalendarCategory[] } | null = null;

export function useCalendar(from: IsoDate, to: IsoDate, includeArchived = false): CalendarState {
    const [range, setRange] = useState<CalendarRange | null>(null);
    const [staff, setStaff] = useState<readonly StaffSummary[]>(referenceCache?.staff ?? []);
    const [categories, setCategories] = useState<readonly CalendarCategory[]>(referenceCache?.categories ?? []);
    const [loading, setLoading] = useState(true);
    const [initialising, setInitialising] = useState(range === null);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    // Guards against a slow response for a range the user has already paged
    // away from overwriting a newer one.
    const latest = useRef(0);

    useEffect(() => {
        let cancelled = false;
        const ticket = ++latest.current;
        setLoading(true);
        setError(null);

        async function load(): Promise<void> {
            const wantsReference = referenceCache === null;
            const [rangeResult, reference] = await Promise.all([
                apiFetch<CalendarRange>(
                    `/calendar?from=${from}&to=${to}${includeArchived ? '&includeArchived=1' : ''}`,
                ),
                wantsReference
                    ? Promise.all([
                          apiFetch<{ staff: readonly StaffSummary[] }>('/staff'),
                          apiFetch<{ categories: readonly CalendarCategory[] }>('/calendar/categories'),
                      ])
                    : Promise.resolve(null),
            ]);

            if (cancelled || ticket !== latest.current) {
                return;
            }
            if (reference !== null) {
                referenceCache = { staff: reference[0].staff, categories: reference[1].categories };
                setStaff(referenceCache.staff);
                setCategories(referenceCache.categories);
            }
            setRange(rangeResult);
        }

        load()
            .catch((err: unknown) => {
                if (cancelled || ticket !== latest.current) {
                    return;
                }
                setError(err instanceof Error ? err.message : 'Could not load the calendar');
            })
            .finally(() => {
                if (cancelled || ticket !== latest.current) {
                    return;
                }
                setLoading(false);
                setInitialising(false);
            });

        return () => {
            cancelled = true;
        };
    }, [from, to, includeArchived, nonce]);

    const reload = useCallback(() => {
        setNonce((n) => n + 1);
    }, []);

    // Derived indexes. Recomputed only when the payload actually changes, so
    // selecting cells or opening the drawer does not re-bucket 1,100 rows.
    const eventsById = useMemo(() => {
        const m = new Map<number, CalendarEvent>();
        for (const e of range?.events ?? []) {
            m.set(e.id, e);
        }
        return m;
    }, [range]);

    const staffById = useMemo(() => {
        const m = new Map<number, StaffSummary>();
        for (const s of staff) {
            m.set(s.staffId, s);
        }
        return m;
    }, [staff]);

    const cells = useMemo(() => {
        const m = new Map<string, CalendarAssignment[]>();
        for (const a of range?.assignments ?? []) {
            // The API already leaves out unfilled slots (legacy 'Select Staff');
            // this keeps them off the board if an older API answers, or a
            // legacy reload puts them back before it is redeployed.
            if (a.staffId === null) {
                continue;
            }
            const key = eventCellKey(a.eventId, a.date);
            const list = m.get(key);
            if (list === undefined) {
                m.set(key, [a]);
            } else {
                list.push(a);
            }
        }
        return m;
    }, [range]);

    return { range, staff, categories, eventsById, staffById, cells, loading, initialising, error, reload };
}

/** Drop the session-level reference cache — call after editing staff or categories. */
export function invalidateCalendarReference(): void {
    referenceCache = null;
}
